import React, { ReactElement } from 'react';
import { Field, FormikActions, FormikProps } from 'formik';
import classNames from 'classnames';

import styles from '../statusUpdateFormStyles.scss';

import InputField from 'buyside/components/presentation/Form/Field/InputField/inputField';
import PostField from 'buyside/components/presentation/Form/Field/PostField/postField';
import SingleCheckboxField from 'buyside/components/presentation/Form/Field/SingleCheckboxField/singleCheckboxField';

import ProfileImage from 'buyside/components/presentation/ProfileImage/profileImage';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import ArticlePreview from 'buyside/components/presentation/Feed/ArticlePreview/articlePreview';

import { OpenGraphDataQuery } from 'interfaces/graphql-typings';

const TermsOfUse = {
  term1:
    'This post in no way incorporates any non-public information and/or unsubstantiated rumors or misrepresentations and does not involve any act, practice, or course of business which operates or would operate as a fraud or deceit upon any person in connection with an investment.',
  term2:
    'I have obtained all necessary approvals from my employer to submit this post to SumZero, and it complies with all applicable policies of my employer.',
  term3:
    'This post does not violate any agreements to which I may be subject (including, without limitation, any confidentiality agreements), insider trading regulations, SEC regulations, and/or other applicable laws, rules and regulations.'
};

enum FormValues {
  ogUrl = 'ogUrl'
}
type Props = {
  closeForm: () => void;
  handleOpenGraphQuery: (
    value: string,
    setFieldValue: FormikActions<{ [key in FormValues]: string }>['setFieldValue']
  ) => void;
  isAdmin: boolean;
  isSubmitting: boolean;
  openGraphData: OpenGraphDataQuery['openGraphData'];
  profileImageUrl: string;
  search: (query: string, callback: (res: string[]) => void) => void;
  termsAccepted: boolean;
  uploadedFiles: string[];
} & FormikProps<{
  asAdmin: boolean;
  body: any;
  files: never[];
  tagList: string;
  term1: any;
  term2: any;
  term3: any;
}>;

class StatusUpdateFields extends React.Component<
  Props,
  { termsOpen: boolean; uploadOpen: boolean }
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      termsOpen: false,
      uploadOpen: false
    };

    this.toggleTermsOpen = this.toggleTermsOpen.bind(this);
  }

  toggleTermsOpen(): void {
    this.setState({ termsOpen: !this.state.termsOpen });
  }

  reset(): void {
    this.setState({ termsOpen: false, uploadOpen: false });
  }

  renderTermsHeader(): ReactElement {
    return (
      <div>
        <h5 className={styles.termsHeader}>SumZero Insight Disclaimers</h5>
        <h6 className={styles.termsText}>
          Please accept the following Disclaimers to post your insight. Your acceptance to the
          following Disclaimers will be pre-checked on future posts.
        </h6>
      </div>
    );
  }

  render(): ReactElement {
    const {
      // Formik props
      isSubmitting,
      dirty,
      errors,
      values,
      // Component props
      closeForm,
      profileImageUrl,
      isAdmin,
      uploadedFiles,
      termsAccepted,
      openGraphData,
      search,
      handleOpenGraphQuery
    } = this.props;
    const forceOpen = dirty && !termsAccepted;
    const checkBoxClassName = classNames(styles.terms, {
      [styles.transitionHidden]: !this.state.termsOpen && !forceOpen
    });
    const termsClassName = classNames(styles.checkbox, styles.acceptedTerms, {
      [styles.transitionHidden]: !termsAccepted
    });
    const disclaimerText = this.state.termsOpen ? 'Hide' : 'Show';
    return (
      <div className={styles.wrap}>
        <ProfileImage source={profileImageUrl} size={'small'} />
        <div className={styles.updateArea}>
          <Field
            component={PostField}
            name="body"
            search={search}
            handleOpenGraphQuery={handleOpenGraphQuery}
          />
          <Field
            component={InputField}
            name="tagList"
            label="Keyword Tags (optional)"
            placeholder="Use commas to separate tags (e.g., social media, acquisition, internet)"
          />
          {values.tagList && (
            <div className={styles.tagInstruction}>
              Use commas to separate tags (e.g., social media, acquisition, internet)
            </div>
          )}
          {!(uploadedFiles.length > 0) && <ArticlePreview {...openGraphData} form />}
          {isAdmin ? (
            <Field name="asAdmin" component={SingleCheckboxField} label="Post as SZ Sytem User" />
          ) : null}
          <div className={checkBoxClassName}>
            {forceOpen && this.renderTermsHeader()}
            <Field
              name="term1"
              component={SingleCheckboxField}
              label={TermsOfUse.term1}
              overrideStyles={styles}
            />
            <Field
              name="term2"
              component={SingleCheckboxField}
              label={TermsOfUse.term2}
              overrideStyles={styles}
            />
            <Field
              name="term3"
              component={SingleCheckboxField}
              label={TermsOfUse.term3}
              overrideStyles={styles}
            />
          </div>
          <div className={styles.formFooter}>
            <div className={termsClassName}>
              You agree to SumZero's Insight Disclaimers (
              <span onClick={this.toggleTermsOpen} className={styles.termsToggle}>
                {disclaimerText} Disclaimers
              </span>
              ).
            </div>
            <Button
              isLoading={isSubmitting}
              text="Cancel"
              type={BUTTON_TYPES.MAJOR}
              size={BUTTON_SIZES.TINY}
              onClick={closeForm}
            />
            {dirty && (
              <Button
                disabled={Boolean(errors.body)}
                isLoading={isSubmitting}
                text="Post"
                type={BUTTON_TYPES.PRIMARY}
                size={BUTTON_SIZES.TINY}
                analyticsTag="post-user-insight"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default StatusUpdateFields;
